/**
 * 搜索事件埋点工具函数
 *
 * 封装搜索事件的埋点逻辑，包括：
 * - 自动更新 SearchContext
 * - 新版上报（TrackerV2）
 * - 统一的数据格式处理
 *
 * ⚠️ 重要：符合原方案设计
 * - 业务代码主动管理 SearchContext
 * - 只传入核心业务数据，其他字段由 Enricher 自动注入
 * - 旧版上报逻辑由业务代码单独调用，不耦合在新版逻辑中
 */

import { SearchContext } from "../context/SearchContext";
import { trackerV2 } from "../core/TrackerV2";

export interface TrackSearchOptions {
  /**
   * 搜索词
   */
  search_word: string;

  /**
   * 搜索结果数量
   */
  search_result_number: number;

  /**
   * 排序类型（可选）
   */
  sort_type?: string;

  /**
   * 颜色筛选（可选）
   */
  colors?: string;

  /**
   * 筛选器一（可选，特殊场景需要明确指定）
   * 例如：["我的采集"]、["我的画板"]、["他的采集"] 等
   */
  filter_one?: string[];
}

/**
 * 搜索事件埋点工具函数
 *
 * @example
 * ```ts
 * // 基础用法
 * trackSearch({
 *   search_word: "设计",
 *   search_result_number: 100,
 *   sort_type: "time",
 *   colors: "red",
 * });
 *
 * // 特殊场景（明确指定 filter_one）
 * trackSearch({
 *   search_word: "设计",
 *   search_result_number: 50,
 *   filter_one: ["我的采集"],
 * });
 * ```
 */
export function trackSearch(options: TrackSearchOptions): void {
  const { search_word, search_result_number, sort_type, colors, filter_one } =
    options;

  // 构建搜索数据（只包含核心业务数据）
  const searchData: Record<string, any> = {
    search_word,
    search_result_number,
  };

  // 可选字段
  if (sort_type !== undefined) {
    searchData.sort_type = sort_type;
  }
  if (colors !== undefined) {
    searchData.colors = colors;
  }
  if (filter_one !== undefined) {
    searchData.filter_one = filter_one;
  }

  // 1. 更新新版 SearchContext（符合原方案设计：业务代码主动管理）
  SearchContext.update({
    search_word,
    search_result_number,
    // 如果传入了 filter_one，也更新到 SearchContext
    ...(filter_one && { filter_one: filter_one[0] || "" }),
  });

  // 2. 更新新版 TrackerV2
  // ⚠️ 注意：只传入核心业务数据，其他字段（filter_one、filter_two、search_word_type等）由 Enricher 自动注入
  trackerV2.track("search", searchData);
}
